import { useCallback, useState } from 'react'
import type { JSX } from 'react'

import { useTranslation } from '../i18n.js'
import { formatWireForDisplay } from '../money.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import { suppressionWord } from '../status-labels.js'

/**
 * Taking an offer at the till. §6.25 `PRM-007`, §15.16, Appendix X.3.
 *
 * **Nothing in this product could do this until migration 0045.** `POST /promotions/orders/:id/
 * application` takes a tax rate per line, `sales_order_line` stored the tax and not the rate, and
 * the rate was typed once into the order form and discarded. So the only caller who could apply an
 * offer was one who still had that form open, and no screen did: a published promotion could never
 * reach a sale. The order now records the rate it used and the service reads it, so this panel
 * sends an order id, the version it is looking at, the shop's rounding, and a coupon code if the
 * customer presented one.
 *
 * **It shows what was suppressed, with the reason.** §15.16 step 4 and X.3 step 5: "The result
 * carries every applied rule **and every suppressed rule with its reason**. The till shows both."
 * A shopkeeper asked why the discount did not come off needs the answer on the screen, and "it did
 * not apply" is not an answer. The explanation is the server's own sentence, which for a margin
 * floor carries the figures when the reader may see them and a plain sentence when they may not.
 *
 * **Once only, and only on a draft.** The service refuses a second apply (`ORDER_ALREADY_PROMOTED`)
 * and refuses anything past `draft`, because the invoice has been issued. So the panel renders on a
 * draft and the refusal is still the server's to give.
 */

export interface ApplyOffersProps {
  readonly orderId: string
  /** Appendix J's lock: the version this screen is looking at. */
  readonly orderVersion: number
  /** Appendix C.1's state. The panel acts only on a draft. */
  readonly status: string
  /** `MR-04` gives no default, so the shop's own rule arrives as a prop or the panel refuses. */
  readonly rounding: string | null
  /** Called once the server has taken the apply, so the order can be read again. */
  readonly onApplied?: () => void
}

interface AppliedAnswer {
  readonly totalPromotion?: { readonly minor: string; readonly currency: string }
  readonly applied?: readonly {
    readonly label: string
    readonly floorOverridden: boolean
    readonly lineIds?: readonly string[]
  }[]
  readonly suppressed?: readonly {
    readonly label: string
    readonly reason: string
    readonly explanation: string
  }[]
  readonly coupons?: readonly {
    readonly code: string
    readonly accepted: boolean
    readonly rejection?: { readonly message: string }
  }[]
}

/** The server's answer, read defensively: it arrives through the queue as `unknown`. */
const answerOf = (response: unknown): AppliedAnswer =>
  typeof response === 'object' && response !== null ? (response as AppliedAnswer) : {}

export function ApplyOffers({
  orderId,
  orderVersion,
  status,
  rounding,
  onApplied,
}: ApplyOffersProps): JSX.Element | null {
  const { t } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [code, setCode] = useState('')
  const [answer, setAnswer] = useState<AppliedAnswer | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [writeId, setWriteId] = useState<string | null>(null)

  useWriteSettled(snapshot, writeId, (write) => {
    setWriteId(null)
    setCode('')
    // §15.16 is the point of the panel, so the answer is kept and rendered rather than discarded.
    setAnswer(answerOf(write.response))
    onApplied?.()
  })

  const apply = useCallback(async () => {
    if (rounding === null) {
      // `PRC-013`: the shop's rounding rule is shop policy and `MR-04` gives no default, so the
      // panel says what is missing rather than choosing a direction for the money.
      setError(t('applyOffers.noRounding'))
      return
    }
    setError(null)
    const write = (await enqueue({
      method: 'POST',
      path: `/promotions/orders/${orderId}/application`,
      label: t('applyOffers.queueLabel'),
      body: {
        expectedOrderVersion: orderVersion,
        rounding,
        // No `taxes`: every line written since 0045 carries the rate it was charged at, and the
        // service reads it. Sending one from here would be retyping a figure the order knows.
        ...(code.trim() === '' ? {} : { couponCodes: [code.trim()] }),
      },
    })) as { id: string }
    setWriteId(write.id)
  }, [code, enqueue, orderId, orderVersion, rounding, t])

  // A draft only. An invoiced order's prices are what the customer was charged.
  if (status !== 'draft') return null

  const line = statusLine(t, snapshot, writeId, t('queue.subject.offerApply'))

  return (
    <section className="lm-apply-offers" aria-labelledby="apply-offers-heading">
      <h3 id="apply-offers-heading" className="lm-apply-offers__heading">
        {t('applyOffers.heading')}
      </h3>

      <label className="lm-field lm-field--inline">
        <span className="lm-field__label">{t('applyOffers.couponLabel')}</span>
        <input
          className="lm-input"
          autoComplete="off"
          value={code}
          onChange={(e) => {
            setCode(e.currentTarget.value)
          }}
        />
      </label>
      {/* `PRM-005`: a coupon is optional. An offer that needs one does not apply without it, and
          the answer below says so by name rather than leaving the discount unexplained. */}
      <p className="lm-hint">{t('applyOffers.couponOptional')}</p>

      {answer === null ? (
        <button type="button" className="lm-button lm-button--primary" onClick={() => void apply()}>
          {t('applyOffers.apply')}
        </button>
      ) : (
        // Once, and the server says so too: a second apply is `ORDER_ALREADY_PROMOTED`, which is a
        // refusal, and a refused write stops the whole queue until somebody discards it. The order
        // stays `draft` after an apply, so nothing else would have stopped a second tap.
        <p className="lm-hint">{t('applyOffers.alreadyTaken')}</p>
      )}

      {error === null ? null : (
        <p className="lm-hint" role="alert">
          {error}
        </p>
      )}

      {line === null ? null : (
        <p className="lm-queue-state" role="status">
          {line}
        </p>
      )}

      {answer === null ? null : (
        <div className="lm-apply-offers__answer">
          {answer.totalPromotion === undefined ? null : (
            <p className="lm-apply-offers__total" dir="ltr">
              {t('applyOffers.tookOff', {
                amount: formatWireForDisplay(
                  answer.totalPromotion.minor,
                  answer.totalPromotion.currency,
                ),
              })}
            </p>
          )}

          {(answer.applied ?? []).length === 0 ? (
            // Not an empty panel: "no offer matched this sale" is the answer, and a blank is not.
            <p className="lm-empty">{t('applyOffers.noneApplied')}</p>
          ) : (
            <ul className="lm-apply-offers__list">
              {(answer.applied ?? []).map((row) => (
                <li key={row.label} className="lm-apply-offers__applied">
                  {row.floorOverridden
                    ? // `PRM-004`: a manager let this one through the margin floor, and the till
                      // says so rather than showing it as an ordinary discount.
                      t('applyOffers.appliedOverridden', { label: row.label })
                    : t('applyOffers.applied', { label: row.label })}
                </li>
              ))}
            </ul>
          )}

          {(answer.suppressed ?? []).length === 0 ? null : (
            <ul className="lm-apply-offers__list">
              {(answer.suppressed ?? []).map((row) => (
                <li key={`${row.label}|${row.reason}`} className="lm-apply-offers__suppressed">
                  {/* The reason in a shopkeeper's words, then the server's own explanation, which
                      is the sentence that names the figures or deliberately does not. */}
                  {t('applyOffers.suppressed', {
                    label: row.label,
                    reason: suppressionWord(t, row.reason),
                    explanation: row.explanation,
                  })}
                </li>
              ))}
            </ul>
          )}

          {(answer.coupons ?? []).map((coupon) => (
            <p key={coupon.code} className="lm-apply-offers__coupon">
              {coupon.accepted
                ? t('applyOffers.couponTaken', { code: coupon.code })
                : t('applyOffers.couponRefused', {
                    code: coupon.code,
                    reason: coupon.rejection?.message ?? t('applyOffers.couponUnknown'),
                  })}
            </p>
          ))}
        </div>
      )}
    </section>
  )
}
